/**
 * <hy-view-toggle>: Grid-/Listenansicht-Umschalter für die Kollektionsseite
 * (sections/hy-main-collection.liquid, M11 Kollektionsseiten-Redesign).
 * Reine Komfortfunktion (CLAUDE.md Regel 7): der Zustand lebt ausschließlich
 * in sessionStorage, nie auf dem Server. Ohne dieses Modul bleiben die
 * Buttons per CSS unsichtbar (visibility: hidden bis [data-hy-view-ready],
 * s. Stylesheet in hy-main-collection.liquid) — die Rasteransicht ist der
 * einzige, vollständig funktionsfähige No-JS-Zustand, kein eigener
 * Ersatzpfad nötig.
 *
 * Zielt über data-grid-id auf ein <ul> per ID statt über DOM-Nachbarschaft
 * (gleiches Entkopplungsmuster wie <hy-facets data-results-id>) — die
 * Umschalter-Buttons sitzen in der Toolbar, das Grid liegt an anderer
 * Stelle im Ergebnis-Fragment.
 *
 * Ein einziger, sitzungsweiter Schlüssel (nicht pro Sektion): ein
 * Umschalten auf einer Kollektionsseite soll für die restliche Sitzung
 * konsistent bleiben, nicht pro Kollektion zurückgesetzt werden.
 *
 * <hy-view-toggle> sitzt in der Toolbar, außerhalb des von assets/hy-facets.js
 * per Section-Rendering-API getauschten Ergebnis-Fragments (#resultsId) —
 * ein Filter-/Sortier-/Seitenwechsel ersetzt also das <ul> (samt der Klasse
 * hy-view-list) durch frisch vom Server gerendertes Markup, ohne dass
 * <hy-view-toggle> selbst erneut verbindet. Deshalb zusätzlich auf das
 * bestehende document-Event hy:filter:updated hören (hy-facets.js dispatcht
 * es nach jedem Swap) und die gespeicherte Ansicht dort erneut anwenden —
 * nutzt das im Theme etablierte hy:-Entkopplungsmuster, ohne hy-facets.js
 * anzufassen (qs-Fund: sonst fiel die Ansicht nach jedem Swap sichtbar auf
 * Grid zurück, während die Buttons weiter "Liste" als gedrückt zeigten).
 */
const STORAGE_KEY = 'hy:collection-view';

class HyViewToggle extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.setAttribute('data-hy-view-ready', '');
    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('hy:filter:updated', this.#applyStoredView, { signal });

    this.#applyStoredView();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #grid() {
    return document.getElementById(this.dataset.gridId);
  }

  #handleClick = (event) => {
    const button = event.target.closest('.hy-view-toggle__button');
    if (!button) return;
    this.#setView(button.dataset.view);
  };

  #applyStoredView = () => {
    let stored = null;
    try {
      stored = window.sessionStorage.getItem(STORAGE_KEY);
    } catch {
      // Komfortfunktion: private Modi/deaktivierter Storage werfen — Grid
      // bleibt einfach die aktive Ansicht (Default), kein Fehlerzustand.
    }
    if (stored === 'list') this.#setView('list');
  };

  #setView(view) {
    const grid = this.#grid;
    if (!grid) return;

    grid.classList.toggle('hy-view-list', view === 'list');

    for (const button of this.querySelectorAll('.hy-view-toggle__button')) {
      button.setAttribute('aria-pressed', String(button.dataset.view === view));
    }

    try {
      window.sessionStorage.setItem(STORAGE_KEY, view);
    } catch {
      // s. o. — Komfortfunktion, kein Fehlerzustand ohne Storage.
    }
  }
}

customElements.define('hy-view-toggle', HyViewToggle);
